/**
 * Client-side calls to the Phase 5 identity API (ARCHITECTURE §9). Cookies (lw_dev/lw_sess/lw_mfa)
 * carry identity; every state-changing request sends the `X-LW-Client: web` CSRF marker (§9.12).
 */
export type Role = 'UAV' | 'FDC' | 'OBSERVER' | 'ADMIN';

const CSRF = { 'content-type': 'application/json', 'x-lw-client': 'web' } as const;

export interface HttpResult<T> {
  status: number;
  ok: boolean;
  body: T | null;
}

interface UADataBrand { brand: string; version: string }
interface UAData {
  getHighEntropyValues(hints: string[]): Promise<{ model?: string; platform?: string; platformVersion?: string; fullVersionList?: UADataBrand[] }>;
}

/** A best-effort rough browser name from a UA string, for engines without userAgentData (Safari/Firefox). */
function browserFromUA(ua: string): string | undefined {
  const m =
    /(Firefox)\/(\d+)/.exec(ua) ??
    /(Edg)\/(\d+)/.exec(ua) ??
    /(OPR)\/(\d+)/.exec(ua) ??
    /(Chrome)\/(\d+)/.exec(ua) ??
    /Version\/(\d+).*(Safari)/.exec(ua);
  if (!m) return undefined;
  // the Safari branch captures version first, name second
  return /Safari/.test(m[0]) && m[2] === 'Safari' ? `Safari ${m[1]}` : `${m[1] === 'OPR' ? 'Opera' : m[1] === 'Edg' ? 'Edge' : m[1]} ${m[2]}`;
}

/**
 * Gather device descriptors to identify the physical device in the admin console (§9.2). No web API
 * exposes a hardware serial; this collects the strongest available signals — the device model (on
 * Android via userAgentData), OS + version, browser + version, screen geometry and timezone — and a
 * friendly label built from them.
 */
export async function deviceDescriptor(): Promise<{ platform: string; screen: string; tz: string; label: string }> {
  const scr = `${screen.width}×${screen.height}${(devicePixelRatio ?? 1) > 1 ? ` @${devicePixelRatio}x` : ''}`;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  let platform = (navigator.platform || '').trim();
  let model = '';
  let browser: string | undefined;
  const uaData = (navigator as unknown as { userAgentData?: UAData }).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const h = await uaData.getHighEntropyValues(['model', 'platform', 'platformVersion', 'fullVersionList']);
      platform = [h.platform, h.platformVersion].filter(Boolean).join(' ') || platform;
      model = h.model ?? '';
      const brand = (h.fullVersionList ?? []).find((b) => !/not.?a.?brand/i.test(b.brand));
      if (brand) browser = `${brand.brand} ${brand.version.split('.')[0]}`;
    } catch {
      // high-entropy hints declined — fall back to the UA string
    }
  }
  if (!browser) browser = browserFromUA(navigator.userAgent);
  const label = [model, browser, platform].filter(Boolean).join(' · ') || navigator.userAgent.slice(0, 60);
  return { platform, screen: scr, tz, label };
}

async function req<T>(method: string, url: string, body?: unknown): Promise<HttpResult<T>> {
  const r = await fetch(url, {
    method,
    headers: method === 'GET' ? { 'x-lw-client': 'web' } : CSRF,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    credentials: 'same-origin',
  });
  let parsed: T | null = null;
  if (r.status !== 204) parsed = (await r.json().catch(() => null)) as T | null;
  return { status: r.status, ok: r.ok, body: parsed };
}

export interface Session {
  user: string;
  role: Role;
  deviceId: string;
  pending: boolean;
  sudo: boolean;
}

export async function getSession(): Promise<Session | null> {
  const r = await req<Session>('GET', '/api/v1/auth/session');
  return r.ok ? r.body : null;
}

export type LoginResult =
  | { kind: 'ok'; role: Role; deviceStatus: 'PENDING' | 'APPROVED'; pairingCode?: string }
  | { kind: 'mfa'; shortId: string; code: string; expiresAt: number }
  | { kind: 'error'; reason: string };

export async function login(username: string, password: string): Promise<LoginResult> {
  const dev = await deviceDescriptor();
  const r = await req<{ status: string; role: Role; deviceStatus: 'PENDING' | 'APPROVED'; pairingCode?: string; shortId: string; code: string; expiresAt: number; error: string }>(
    'POST',
    '/api/v1/auth/login',
    { username, password, ...dev },
  );
  if (r.ok && r.body?.status === 'ok') return { kind: 'ok', role: r.body.role, deviceStatus: r.body.deviceStatus, ...(r.body.pairingCode ? { pairingCode: r.body.pairingCode } : {}) };
  if (r.status === 200 && r.body?.status === 'mfa_required') return { kind: 'mfa', shortId: r.body.shortId, code: r.body.code, expiresAt: r.body.expiresAt };
  return { kind: 'error', reason: r.body?.error ?? (r.status === 429 ? 'rate_limited' : 'bad_credentials') };
}

export async function enroll(token: string): Promise<LoginResult> {
  const dev = await deviceDescriptor();
  const r = await req<{ status: string; role: Role; deviceStatus: 'PENDING'; pairingCode?: string; error: string }>('POST', '/api/v1/auth/enroll', { token, ...dev });
  if (r.ok && r.body?.status === 'ok') return { kind: 'ok', role: r.body.role, deviceStatus: 'PENDING', ...(r.body.pairingCode ? { pairingCode: r.body.pairingCode } : {}) };
  return { kind: 'error', reason: r.body?.error ?? 'token_invalid' };
}

export async function mfaPoll(): Promise<'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED' | 'UNKNOWN'> {
  const r = await req<{ status: 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED' | 'UNKNOWN' }>('POST', '/api/v1/auth/mfa/poll');
  return r.body?.status ?? 'UNKNOWN';
}

export async function mfaTotp(code: string): Promise<boolean> {
  const r = await req<{ status: string }>('POST', '/api/v1/auth/mfa/totp', { code });
  return r.ok;
}

export async function logout(): Promise<void> {
  await req('POST', '/api/v1/auth/logout');
}

/** Re-enter sudo mode by re-typing the password (§9.6). */
export async function sudo(password: string): Promise<boolean> {
  const r = await req('POST', '/api/v1/auth/sudo', { password: password.replace(/[\s-]/g, '') });
  return r.ok;
}

// ── admin ──────────────────────────────────────────────────────────────────────────────────────
export async function adminGet<T>(path: string): Promise<T | null> {
  const r = await req<T>('GET', `/api/v1/admin/${path}`);
  return r.ok ? r.body : null;
}
export async function adminPost<T = unknown>(path: string, body?: unknown): Promise<HttpResult<T>> {
  return req<T>('POST', `/api/v1/admin/${path}`, body ?? {});
}
export async function adminDelete<T = unknown>(path: string): Promise<HttpResult<T>> {
  return req<T>('DELETE', `/api/v1/admin/${path}`);
}
